import {
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  useTheme,
  Box,
} from "@mui/material";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Vector3 } from "three";
import {
  EDisplayType,
  EViewerMode,
  ProjectDetailContext,
} from "../Interfaces/ProjectDetailInterfaces";
import {
  IMeasurementHandler,
  IMeasurementPanelPosition,
  MeasurementType,
} from "../../../viewer/measurement/types";
import { hasPanelPositionChanged } from "../../../viewer/measurement/measurementResultPanelCommands";
import ProjectVolumeCalcMenu from "./ProjectVolumeCalcMenu";
import { VolumeCalcColumn } from "../BottomDrawer/CalcDrawerContent";
import CalcContext from "../Contexts/CalcContext";
import { Q3DDataTable, Q3DDataTableHeaderType } from "../../Parts/Q3DDataTable";
import Q3DIconButton from "../../Parts/Q3DIconButton";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { measurementPointsToTableRows } from "./measurementPointsToTableRows";

interface ICalResProps {
  selectedItem: IMeasurementHandler;
  handleClickCancel: () => void;
  showHeatMap: boolean;
  rowsVolumeCalc: VolumeCalcColumn[];
  setRowsVolumeCalc: Dispatch<SetStateAction<VolumeCalcColumn[]>>;
  // editType: string;
  // setEditType: Dispatch<SetStateAction<string>>;
  startEdit: () => void;
  finishEdit: () => void;
  // EditType: { name: string }[];
  /** "Edit (Measurement Result)" mode is active (SC_QUICK-2187, QU3DW-1367). */
  isEditingMeasurementResult: boolean;
  panelPositionBeforeEdit?: IMeasurementPanelPosition;
  panelPositionSaving: boolean;
  onResetPanelPosition: () => void;
  /**
   * Reports the live panel position + whether it changed, so the Tick/X
   * buttons in the measurement toolbar (CalcTabPanel) can enable/disable
   * themselves. Confirm/cancel themselves live in CalcDrawerContent.
   */
  onPanelPositionChange: (changed: boolean) => void;
}

/** Row id of the "Measurement Result Position" row — not a measurement point. */
const MEASUREMENT_RESULT_POSITION_ROW_ID = "measurement-result-position";

const CalcResultDetail = (calcResProps: ICalResProps) => {
  const { viewer, currentViewerMode, projectDetail, displayType } =
    useContext(ProjectDetailContext);
  const { onEditingMode } = useContext(CalcContext);

  /*****************************************
      state
    ******************************************/

  const { t, i18n } = useTranslation();

  // "Measurement Result Position" row (SC_QUICK-2187, QU3DW-1367) — X/Y/Z
  // as reported by the 3D viewer (already computed there, AC-16). Polled
  // while dragging since panel movement happens inside the render loop, not
  // as React state.
  const [panelPosition, setPanelPosition] =
    useState<IMeasurementPanelPosition>();

  /*****************************************
      Effect
    ******************************************/
  // useEffect(() => {
  //   if (currentViewerMode === EViewerMode.CALCULATION)
  //     calcResProps.setEditType(calcResProps.EditType[0].name);
  // }, [currentViewerMode]);

  // QU3DW-1379 (SC_QUICK-2063): reused by both effects below so Cancel (which
  // restores selectedItem._points to the last-saved value — see
  // MeasurementManager.cancelEditingMeasurement) refreshes the table with the
  // exact same conversion used on initial selection, not a stale copy.
  const refreshRowsFromSelectedItem = () => {
    const offset = viewer.getOffset();
    calcResProps.setRowsVolumeCalc(
      measurementPointsToTableRows(
        calcResProps.selectedItem?._points,
        offset,
      ) as unknown as VolumeCalcColumn[],
    );
  };

  useEffect(() => {
    if (onEditingMode) calcResProps.startEdit();

    return () => {
      // onEditingMode was true and is now turning false — via Cancel (points
      // just restored) or Confirm (points just saved). Either way,
      // selectedItem._points now holds the correct value to display.
      if (onEditingMode) refreshRowsFromSelectedItem();
    };
  }, [onEditingMode]);

  useEffect(() => {
    refreshRowsFromSelectedItem();

    return () => {
      calcResProps.setRowsVolumeCalc([]);
    };
  }, [calcResProps.selectedItem]);

  useEffect(() => {
    const readPanelPosition = () => {
      const id = calcResProps.selectedItem?.id;
      if (id === undefined) return;
      const position = viewer.getPanelPosition(id);
      if (!position) return;
      setPanelPosition({ x: position.x, y: position.y, z: position.z });
    };

    readPanelPosition();
    if (!calcResProps.isEditingMeasurementResult) return;

    const intervalId = setInterval(readPanelPosition, 200);
    return () => clearInterval(intervalId);
  }, [calcResProps.selectedItem, calcResProps.isEditingMeasurementResult]);

  const panelPositionChanged = hasPanelPositionChanged(
    panelPosition,
    calcResProps.panelPositionBeforeEdit,
  );

  useEffect(() => {
    calcResProps.onPanelPositionChange(panelPositionChanged);
  }, [panelPositionChanged]);

  /*****************************************
      function
    ******************************************/
  const selectedItemData = () => {
    return calcResProps.selectedItem?.type === MeasurementType.TWO_POINT ? (
      <div>
        <Typography
          padding={"0px 20px 0px 10px"}
          variant="body2"
          gutterBottom
          style={customColor}
        >
          {t("HORIZONTAL")}
          <span style={{ float: "right" }}>
            {Math.sqrt(
              (calcResProps.selectedItem._points[0].x -
                calcResProps.selectedItem._points[1].x) **
                2 +
                (calcResProps.selectedItem._points[0].y -
                  calcResProps.selectedItem._points[1].y) **
                  2,
            ).toFixed(3)}{" "}
            m
          </span>
        </Typography>
        <Typography
          padding={"0px 20px 0px 10px"}
          variant="body2"
          gutterBottom
          style={customColor}
        >
          {t("VERTICAL")}
          <span style={{ float: "right" }}>
            {Math.abs(
              calcResProps.selectedItem._points[0].z -
                calcResProps.selectedItem._points[1].z,
            ).toFixed(3)}{" "}
            m
          </span>
        </Typography>
      </div>
    ) : (
      <div></div>
    );
  };

  const resCalcType = (data: any) => {
    if (!data) return;
    const type: string = data.type;
    if (type === MeasurementType.VOLUME_CALCULATION) {
      return t("VOLUME_CALC");
    } else if (type === MeasurementType.TWO_POINT) {
      return t("DISTANCE_TWO_POINTS");
    } else if (type === MeasurementType.MULTIPLE_POINT) {
      return t("DISTANCE_MULTIPLE_POINTS");
    } else if (type === MeasurementType.VERTICAL) {
      return t("VERTICAL_DISTANCE");
    } else if (type === MeasurementType.HORIZONTAL) {
      return t("HORIZONTAL_DISTANCE");
    } else if (type === MeasurementType.FLAT_AREA_CALCULATION) {
      return t("FLAT_AREA_CALC");
    } else if (type === MeasurementType.SURFACE) {
      return t("SURFACE_DISTANCE");
    }
  };

  /**
   * Applies a value typed into (or stepped in) the Measurement Result Position
   * row back onto the 3D panel (QU3DW-1389). Table values are offset-relative,
   * the viewer works in absolute world coordinates.
   */
  const updatePanelPositionFromTable = (key: string, value: any) => {
    const id = calcResProps.selectedItem?.id;
    if (id === undefined || !panelPosition) return;

    const next = Number(value);
    if (!Number.isFinite(next)) return;

    const offset = viewer.getOffset();
    const absolute = {
      x: key === "x" ? next + offset.x : panelPosition.x,
      y: key === "y" ? next + offset.y : panelPosition.y,
      z: key === "z" ? next + offset.z : panelPosition.z,
    };

    viewer.setPanelPosition(id, new Vector3(absolute.x, absolute.y, absolute.z));
    viewer.updateRenderer();
    setPanelPosition(absolute);
  };

  const updateMeasurement = (id: string, key: string, value: any) => {
    // The Measurement Result Position row is not a measurement point — editing
    // it moves the panel instead of the measured geometry (QU3DW-1389).
    if (id === MEASUREMENT_RESULT_POSITION_ROW_ID) {
      updatePanelPositionFromTable(key, value);
      return;
    }

    const offset = viewer.getOffset();
    const points = calcResProps.rowsVolumeCalc.map((data) => {
      if (id === data.id) {
        switch (key) {
          case "x":
            return new Vector3(Number(value), data.y, data.z).add(offset);
          case "y":
            return new Vector3(data.x, Number(value), data.z).add(offset);
          case "z":
            return new Vector3(data.x, data.y, Number(value)).add(offset);
          default:
            return new Vector3(data.x, data.y, data.z).add(offset);
        }
      } else {
        return new Vector3(data.x, data.y, data.z).add(offset);
      }
    });
    viewer.setPointsToMeasurement(calcResProps.selectedItem.id, points);
    const pointsList = calcResProps.selectedItem._points.map(
      (data: any, index: any) => {
        return {
          id: index,
          pointName: index + 1,
          x: data.x - offset.x,
          y: data.y - offset.y,
          z: data.z - offset.z,
        };
      },
    ) as VolumeCalcColumn[];
    calcResProps.setRowsVolumeCalc(pointsList);
    calcResProps.startEdit();
  };

  const theme = useTheme();

  // The two Edit options must not bleed into each other (QU3DW-1386):
  // "Edit" edits the measurement point coordinates, "Edit (Measurement
  // Result)" edits only the Measurement Result Position row.
  const isEditingPoints =
    onEditingMode && !calcResProps.isEditingMeasurementResult;

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customBackground = {
    background: theme.palette.base.modal,
  };

  const headers = [
    {
      key: "pointName",
      name: t("POINT_NAME"),
      type: "text",
    },
    {
      key: "y",
      name: t("X_COORDINATE"),
      type: isEditingPoints ? "numberInput" : "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "x",
      name: t("Y_COORDINATE"),
      type: isEditingPoints ? "numberInput" : "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "z",
      name: t("Z_COORDINATE"),
      type: isEditingPoints ? "numberInput" : "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
  ] as Q3DDataTableHeaderType[];

  const calculateVolumeDifferences = useMemo(() => {
    const { rowsVolumeCalc, selectedItem } = calcResProps;
    if (!rowsVolumeCalc || !selectedItem?.type) return undefined;

    const types = [
      MeasurementType.TWO_POINT,
      MeasurementType.VERTICAL,
      MeasurementType.HORIZONTAL,
      MeasurementType.SURFACE,
    ];

    if (types.includes(selectedItem.type)) {
      if (rowsVolumeCalc.length < 2) return rowsVolumeCalc;

      const [first, second] = rowsVolumeCalc;

      return [
        ...rowsVolumeCalc,
        {
          id: 2,
          pointName: "1-2",
          x: first.x - second.x,
          y: first.y - second.y,
          z: first.z - second.z,
        },
      ];
    }

    return rowsVolumeCalc;
  }, [calcResProps]);

  // "Measurement Result Position" row (SC_QUICK-2187, QU3DW-1367) — rendered
  // as the last row of the point table, with the Reset icon next to its label
  // (AC-16). Coordinates follow the same offset + column mapping as the point
  // rows above (the X column reads `y`, the Y column reads `x`).
  const rowsWithPanelPosition = useMemo(() => {
    if (!calculateVolumeDifferences || !panelPosition) {
      return calculateVolumeDifferences;
    }

    const offset = viewer.getOffset();

    return [
      ...calculateVolumeDifferences,
      {
        id: MEASUREMENT_RESULT_POSITION_ROW_ID,
        pointName: (
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {t("MEASUREMENT_RESULT_POSITION")}
            {calcResProps.isEditingMeasurementResult &&
              panelPositionChanged && (
                <Q3DIconButton
                  title={t("RESET")}
                  onClick={calcResProps.onResetPanelPosition}
                  icon={<RestartAltIcon sx={{ fontSize: "16px" }} />}
                  height={"20px"}
                  width={"20px"}
                  disabledBorder={true}
                  disabled={calcResProps.panelPositionSaving}
                />
              )}
          </Box>
        ),
        x: panelPosition.x - offset.x,
        y: panelPosition.y - offset.y,
        z: panelPosition.z - offset.z,
        // Always set explicitly: leaving this undefined would fall back to the
        // column type, which is "numberInput" during a normal point edit and
        // would make this row editable from the wrong Edit option (QU3DW-1386).
        // Editable only under "Edit (Measurement Result)" (QU3DW-1389).
        cellTypes: calcResProps.isEditingMeasurementResult
          ? { x: "numberInput", y: "numberInput", z: "numberInput" }
          : { x: "number", y: "number", z: "number" },
      },
    ];
  }, [
    calculateVolumeDifferences,
    panelPosition,
    panelPositionChanged,
    calcResProps.isEditingMeasurementResult,
    calcResProps.panelPositionSaving,
  ]);

  return (
    <>
      <Q3DDataTable
        headers={headers}
        rows={rowsWithPanelPosition}
        onChange={updateMeasurement}
        ContainerProps={{
          sx: { border: `1px solid ${theme.palette.base.contrast}`, flex: 3 },
        }}
      />
    </>
  );
};

export default CalcResultDetail;
